import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "@/lib/api";
import { PageHeader } from "@/components/admin-ui";
import { useToast } from "@/components/ui-prims";

type UserRow = {
  id: string;
  email: string;
  name: string;
  role: string;          // ordinary | pro
  status: string;        // active | suspended
  email_verified: boolean;
  google_linked: boolean;
  last_login_at?: string | null;
  created_at: string;
  conversations: number;
  tokens_in_30d: number;
  tokens_out_30d: number;
};

type ListResponse = {
  users: UserRow[];
  total: number;
  page: number;
  limit: number;
};

type UserDetailResponse = UserRow & {
  usage_by_day: Array<{ day: string; input: number; output: number }>;
};

export function UsersPage() {
  const [data, setData] = useState<ListResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<string>("");
  const [role, setRole] = useState<string>("");
  const [page, setPage] = useState(1);

  // Debounce search typing — without this, the network panel hammers
  // /admin/accounts/users on every keystroke.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setQ(qInput.trim());
      setPage(1);
    }, 250);
    return () => window.clearTimeout(t);
  }, [qInput]);

  const [selected, setSelected] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      if (role) params.set("role", role);
      params.set("page", String(page));
      const res = await api.get<ListResponse>(`/admin/accounts/users?${params.toString()}`);
      setData(res);
      setErr(null);
    } catch (e: any) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [q, status, role, page]);

  useEffect(() => { void refresh(); }, [refresh]);

  const limit = data?.limit ?? 25;
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <>
      <PageHeader
        kicker="Accounts"
        title="Users"
        sub="Every account, buyer and pro. Click a row for usage detail, suspend toggle, and login history."
        actions={
          <>
            <input
              placeholder="Search email or name"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              style={searchInput}
            />
            <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} style={select}>
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
            <select value={role} onChange={(e) => { setRole(e.target.value); setPage(1); }} style={select}>
              <option value="">All roles</option>
              <option value="ordinary">Buyer</option>
              <option value="pro">Pro / agent</option>
            </select>
          </>
        }
      />

      {err && <div style={{ ...errBanner, margin: "0 26px 14px" }}>{err}</div>}

      <div style={{ ...tableCard, margin: "0 26px" }}>
        <div style={hdrRow}>
          <div>Email</div>
          <div>Name</div>
          <div>Role</div>
          <div>Status</div>
          <div style={{ textAlign: "right" }}>Convos</div>
          <div style={{ textAlign: "right" }}>Tokens 30d</div>
          <div style={{ textAlign: "right" }}>Last login</div>
          <div style={{ textAlign: "right" }}>Created</div>
        </div>
        {loading && !data && <Empty>Loading…</Empty>}
        {data?.users?.map((u) => (
          <div key={u.id} style={dataRow} onClick={() => setSelected(u.id)}>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {u.email}
              {!u.email_verified && <span title="Not verified" style={dot}>•</span>}
            </div>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.name || "—"}</div>
            <div><RoleBadge role={u.role} /></div>
            <div><StatusBadge status={u.status} /></div>
            <div style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 12 }}>{u.conversations.toLocaleString()}</div>
            <div style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--ink-3)" }}>
              {(u.tokens_in_30d + u.tokens_out_30d).toLocaleString()}
            </div>
            <div style={{ textAlign: "right", fontSize: 11.5, color: "var(--ink-3)" }}>{fmtTime(u.last_login_at)}</div>
            <div style={{ textAlign: "right", fontSize: 11.5, color: "var(--ink-3)" }}>{fmtDate(u.created_at)}</div>
          </div>
        ))}
        {data && (data.users?.length ?? 0) === 0 && !loading && (
          <Empty>No users match.</Empty>
        )}
      </div>

      <div style={{ margin: "14px 26px 26px", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "var(--ink-3)" }}>
        <div>{total} user{total === 1 ? "" : "s"}</div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} style={pagerBtn}>‹ Prev</button>
          <span>Page {page} / {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={pagerBtn}>Next ›</button>
        </div>
      </div>

      {selected && (
        <UserDrawer id={selected} onClose={() => setSelected(null)} onChanged={() => { void refresh(); }} />
      )}
    </>
  );
}

function UserDrawer({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const [u, setU] = useState<UserDetailResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      const data = await api.get<UserDetailResponse>(`/admin/accounts/users/${encodeURIComponent(id)}`);
      setU(data);
      setErr(null);
    } catch (e: any) {
      setErr(e instanceof ApiError ? e.message : String(e));
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  const toggleSuspend = async () => {
    if (!u || busy) return;
    setBusy(true);
    const wasSuspended = u.status === "suspended";
    try {
      const action = wasSuspended ? "unsuspend" : "suspend";
      await api.post(`/admin/accounts/users/${encodeURIComponent(u.id)}/${action}`);
      toast(wasSuspended ? "Account unsuspended" : "Account suspended", { tone: "success" });
      await load();
      onChanged();
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setErr(msg);
      toast(`${wasSuspended ? "Unsuspend" : "Suspend"} failed: ${msg}`, { tone: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div onClick={onClose} style={drawerScrim}>
      <div onClick={(e) => e.stopPropagation()} style={drawerPanel}>
        <header style={drawerHeader}>
          <div>
            <div style={kicker}>User</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 14, color: "var(--ink)" }}>{u?.email ?? "…"}</div>
          </div>
          <button onClick={onClose} style={ghostBtn}>Close</button>
        </header>

        <div style={{ padding: 20, overflowY: "auto" }}>
          {err && <div style={errBanner}>{err}</div>}
          {!u && !err && <div style={{ color: "var(--ink-3)", fontStyle: "italic" }}>Loading…</div>}
          {u && (
            <>
              <div style={statsGrid}>
                <Stat label="Status"><StatusBadge status={u.status} /></Stat>
                <Stat label="Role"><RoleBadge role={u.role} /></Stat>
                <Stat label="Verified">{u.email_verified ? "Yes" : "No"}</Stat>
                <Stat label="Google">{u.google_linked ? "Linked" : "—"}</Stat>
              </div>

              <div style={{ marginTop: 18, padding: "12px 14px", border: "1px solid var(--rule)", borderRadius: 8, background: "var(--surface)" }}>
                <div style={kicker}>Identity</div>
                <Row label="Name">{u.name || "—"}</Row>
                <Row label="User ID" mono>{u.id}</Row>
                <Row label="Created">{fmtTime(u.created_at)}</Row>
                <Row label="Last login">{u.last_login_at ? fmtTime(u.last_login_at) : "—"}</Row>
              </div>

              <div style={{ marginTop: 18, padding: "12px 14px", border: "1px solid var(--rule)", borderRadius: 8, background: "var(--surface)" }}>
                <div style={kicker}>Activity (last 30d)</div>
                <Row label="Conversations">{u.conversations.toLocaleString()}</Row>
                <Row label="Input tokens" mono>{u.tokens_in_30d.toLocaleString()}</Row>
                <Row label="Output tokens" mono>{u.tokens_out_30d.toLocaleString()}</Row>
                <TokensChart series={u.usage_by_day} />
              </div>

              <div style={{ marginTop: 18, display: "flex", gap: 8 }}>
                <button onClick={toggleSuspend} disabled={busy} style={u.status === "suspended" ? primaryBtn : dangerBtn}>
                  {busy ? "Working…" : (u.status === "suspended" ? "Unsuspend" : "Suspend account")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TokensChart({ series }: { series: Array<{ day: string; input: number; output: number }> }) {
  const max = Math.max(1, ...series.map((s) => s.input + s.output));
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 60 }}>
        {series.map((s) => {
          const total = s.input + s.output;
          const h = Math.max(2, Math.round((total / max) * 60));
          return (
            <div key={s.day} title={`${s.day}: ${total.toLocaleString()} tokens`} style={{
              flex: 1, height: h, background: total > 0 ? "var(--accent)" : "var(--surface-3)",
              borderRadius: 1,
            }} />
          );
        })}
      </div>
      <div style={{ marginTop: 4, display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--ink-4)" }}>
        <span>{series[0]?.day}</span>
        <span>{series[series.length - 1]?.day}</span>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === "active"    ? { bg: "rgba(29,74,58,0.10)",  fg: "var(--pos)" }
  : status === "suspended" ? { bg: "rgba(122,45,31,0.10)", fg: "var(--neg)" }
  :                          { bg: "var(--surface-2)",      fg: "var(--ink-3)" };
  return <Badge bg={tone.bg} fg={tone.fg}>{status}</Badge>;
}

function RoleBadge({ role }: { role: string }) {
  const tone = role === "pro"
    ? { bg: "rgba(107,83,32,0.10)", fg: "var(--ai-accent)" }
    : { bg: "var(--surface-2)",       fg: "var(--ink-3)" };
  return <Badge bg={tone.bg} fg={tone.fg}>{role === "pro" ? "Pro" : "Buyer"}</Badge>;
}

function Badge({ bg, fg, children }: { bg: string; fg: string; children: React.ReactNode }) {
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 4,
      fontSize: 10.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase",
      background: bg, color: fg,
    }}>{children}</span>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={kicker}>{label}</div>
      <div style={{ fontSize: 13.5, color: "var(--ink)", fontWeight: 500, marginTop: 4 }}>{children}</div>
    </div>
  );
}

function Row({ label, children, mono }: { label: string; children: React.ReactNode; mono?: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderTop: "1px solid var(--rule)", fontSize: 12.5, gap: 12 }}>
      <span style={{ color: "var(--ink-3)" }}>{label}</span>
      <span style={{ fontFamily: mono ? "var(--font-mono)" : undefined, color: "var(--ink-2)", textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{children}</span>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: 28, textAlign: "center", color: "var(--ink-3)", fontStyle: "italic", fontSize: 13 }}>{children}</div>;
}

function fmtTime(s?: string | null) {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("en-SG", { dateStyle: "short", timeStyle: "short" });
}
function fmtDate(s?: string | null) {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString("en-SG", { day: "2-digit", month: "short", year: "2-digit" });
}

const kicker: React.CSSProperties = { fontSize: 9.5, color: "var(--ink-4)", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase" };
const searchInput: React.CSSProperties = { padding: "6px 10px", borderRadius: 6, border: "1px solid var(--rule)", background: "var(--surface)", color: "var(--ink-2)", fontSize: 12.5, minWidth: 220 };
const select: React.CSSProperties = { padding: "6px 10px", borderRadius: 6, border: "1px solid var(--rule)", background: "var(--surface)", color: "var(--ink-2)", fontSize: 12 };
const tableCard: React.CSSProperties = { border: "1px solid var(--rule)", borderRadius: 10, background: "var(--surface)", overflow: "hidden" };
const hdrRow: React.CSSProperties = { display: "grid", gridTemplateColumns: "1.5fr 1fr 80px 90px 80px 110px 130px 110px", gap: 12, padding: "10px 14px", color: "var(--ink-4)", fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", borderBottom: "1px solid var(--rule)", background: "var(--surface-2)" };
const dataRow: React.CSSProperties = { display: "grid", gridTemplateColumns: "1.5fr 1fr 80px 90px 80px 110px 130px 110px", gap: 12, padding: "11px 14px", alignItems: "center", borderBottom: "1px solid var(--rule)", cursor: "pointer", fontSize: 12.5 };
const errBanner: React.CSSProperties = { background: "rgba(122,45,31,0.08)", color: "var(--neg)", padding: "10px 14px", borderRadius: 8, marginBottom: 14, fontSize: 13 };
const dot: React.CSSProperties = { marginLeft: 6, color: "var(--warn)" };

const drawerScrim: React.CSSProperties = { position: "fixed", inset: 0, background: "rgba(23,32,29,0.32)", zIndex: 50, display: "flex", justifyContent: "flex-end" };
const drawerPanel: React.CSSProperties = { width: "min(560px, 92vw)", height: "100%", background: "var(--bg)", display: "flex", flexDirection: "column", boxShadow: "-8px 0 24px rgba(23,32,29,0.10)" };
const drawerHeader: React.CSSProperties = { padding: "14px 20px", borderBottom: "1px solid var(--rule)", display: "flex", justifyContent: "space-between", alignItems: "center" };
const ghostBtn: React.CSSProperties = { border: "1px solid var(--rule)", background: "transparent", color: "var(--ink-2)", padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12 };
const pagerBtn: React.CSSProperties = { padding: "4px 10px", borderRadius: 5, border: "1px solid var(--rule)", background: "var(--surface)", color: "var(--ink-2)", cursor: "pointer", fontSize: 12 };
const primaryBtn: React.CSSProperties = { padding: "8px 14px", borderRadius: 6, border: "none", background: "var(--accent)", color: "#f4f1ea", cursor: "pointer", fontSize: 13, fontWeight: 500 };
const dangerBtn: React.CSSProperties = { padding: "8px 14px", borderRadius: 6, border: "1px solid var(--neg)", background: "transparent", color: "var(--neg)", cursor: "pointer", fontSize: 13, fontWeight: 500 };
const statsGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 };
